/** @format */
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TimelineControls, UserSettings } from 'common-typescript/types';
import * as _ from 'lodash-es';
import { BehaviorSubject, combineLatest, defer, distinctUntilChanged, map, Observable, of, shareReplay, switchMap, take } from 'rxjs';
import { mono, Mono, RequestQueue, requestQueueSequential } from 'sis-common';
import { AppErrorHandler, UserSettingsEntityService } from 'sis-components';

type ControlName = keyof TimelineControls;

const DEFAULT_CONTROLS: TimelineControls = { showEmptySummerPeriods: false, showPastStudyTerms: false, useHorizontalLayout: false };

@Injectable({ providedIn: 'root' })
export class TimelineControlsService {
    private readonly userSettingsEntityService = inject(UserSettingsEntityService);
    private readonly appErrorHandler = inject(AppErrorHandler);

    // Shared so that the control observables don't each trigger their own settings fetch while the store is still empty
    private readonly storedControls$: Observable<TimelineControls> = defer(() => this.userSettingsEntityService.getOwnSettings()).pipe(
        map(settings => ({ ...DEFAULT_CONTROLS, ...settings?.timelineControls })),
        this.appErrorHandler.defaultErrorHandler(DEFAULT_CONTROLS),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    // The user's changes take precedence over the stored controls, so that a failed save doesn't change what is shown
    private readonly localChanges$ = new BehaviorSubject<Partial<TimelineControls>>({});

    private readonly timelineControls$: Observable<TimelineControls> = combineLatest([this.storedControls$, this.localChanges$]).pipe(
        map(([storedControls, localChanges]) => ({ ...storedControls, ...localChanges })),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    // Saves are executed one at a time, so that an older save can never overwrite a newer one in the database
    private readonly saveQueue: RequestQueue<void, UserSettings> = requestQueueSequential<void, UserSettings>(
        () => this.saveLocalChanges(),
        () => {},
        this.appErrorHandler.defaultErrorHandler(),
        takeUntilDestroyed(inject(DestroyRef)),
    );

    readonly showEmptySummerPeriods$: Observable<boolean> = this.readControl('showEmptySummerPeriods');
    readonly showPastStudyTerms$: Observable<boolean> = this.readControl('showPastStudyTerms');
    readonly useHorizontalLayout$: Observable<boolean> = this.readControl('useHorizontalLayout');

    setShowEmptySummerPeriods(value: boolean): void {
        this.updateControl('showEmptySummerPeriods', value);
    }

    setShowPastStudyTerms(value: boolean): void {
        this.updateControl('showPastStudyTerms', value);
    }

    setUseHorizontalLayout(value: boolean): void {
        this.updateControl('useHorizontalLayout', value);
    }

    private readControl(controlName: ControlName): Observable<boolean> {
        return this.timelineControls$.pipe(
            map(controls => controls[controlName]),
            distinctUntilChanged(),
        );
    }

    private updateControl(controlName: ControlName, value: boolean): void {
        this.localChanges$.next({ ...this.localChanges$.getValue(), [controlName]: value });
        this.saveQueue.next();
    }

    private saveLocalChanges(): Mono<UserSettings> {
        return mono(
            this.userSettingsEntityService.getOwnSettings().pipe(
                take(1),
                switchMap(settings => {
                    // The local changes are read only when the save is executed, so that the save contains the latest state
                    // (including changes whose earlier save has failed) on top of the latest stored settings
                    const storedControls = { ...DEFAULT_CONTROLS, ...settings?.timelineControls };
                    const localChanges = this.localChanges$.getValue();
                    // Skip the save if an earlier save in the queue has already stored the latest state
                    if (_.isMatch(storedControls, localChanges)) {
                        return of(settings);
                    }
                    const updatedSettings: Partial<UserSettings> = _.cloneDeep(settings) ?? {};
                    updatedSettings.timelineControls = { ...storedControls, ...localChanges };
                    return this.userSettingsEntityService.saveOwnSettings(updatedSettings);
                }),
            ),
        );
    }
}
